import React, { forwardRef } from "react";
import Icon from "../AppIcon";

const Input = forwardRef(({ className = "", type = "text", iconName, label, id, onChange, error, required, fullWidth = true, helperText, ...props }, ref) => {
    // Handle onChange to pass the full event object to parent
    const handleChange = (e) => {
        if (onChange) {
            onChange(e);
        }
    };

    // CheckBox-specific styles
    if (type === "checkbox") {
        const checkboxClass =
            "h-4 w-4 mx-1 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

        return (
            <input
                type="checkbox"
                className={checkboxClass}
                ref={ref}
                onChange={handleChange}
                {...props}
            />
        );
    }

    // Radio button-specific styles
    if (type === "radio") {
        const radioClass =
            "h-4 w-4 mx-1 rounded-full border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

        return (
            <input
                type="radio"
                className={`${radioClass} ${className}`}
                ref={ref}
                onChange={handleChange}
                {...props}
            />
        );
    }

    const baseClass = error 
        ? "flex h-10 rounded-md border border-red-500 bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
        : "flex h-10 rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm";
    
    // Add width class based on fullWidth prop
    const widthClass = fullWidth ? "w-full" : "w-auto";

    // Function to render the input element based on icon presence
    const renderInput = () => {
        if (iconName) {
            return (
                <div className="relative">
                    <input
                        type={type}
                        id={id}
                        className={`${baseClass} ${widthClass} ${className} pl-10`}
                        ref={ref}
                        onChange={handleChange}
                        {...props}
                    />
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <Icon name={iconName} size={18} className="text-gray-400" />
                    </div>
                </div>
            );
        }

        return (
            <input
                type={type}
                id={id}
                className={`${baseClass} ${widthClass} ${className}`}
                ref={ref}
                onChange={handleChange}
                {...props}
            />
        );
    };

    // If label is provided, render with label, otherwise just the input
    if (label) {
        return (
            <div className="space-y-2">
                <label 
                    htmlFor={id} 
                    className="block text-sm font-medium text-gray-700"
                >
                    {label}
                    {required && <span className="text-red-500 ml-1">*</span>}
                </label>
                {renderInput()}
                {error && (
                    <p className="text-sm text-red-600 flex items-center gap-1">
                        <Icon name="AlertCircle" size={14} />
                        {error}
                    </p>
                )}
                {helperText && !error && (
                    <p className="text-sm text-gray-500">
                        {helperText}
                    </p>
                )}
            </div>
        );
    }

    // Just render the input without a label
    return renderInput();
});

Input.displayName = "Input";

export default Input;
